import { useAppSelector } from '../../../hooks/useAppSelector';
import * as monitoringSlice from '../../../data/monitoring.slice';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import WorkflowStep, { WorkflowStepRef } from './WorkflowStep';
import { useEffect, useRef, useState } from 'react';
import AddWorkFlowDialog from './AddWorkflowDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import workflowUtils, {
  CreateWorkflowData,
  WorkflowError,
} from '../../../services/workflowUtils';
import { WorkflowStepFormData } from '../../../data/workflow-form.model';
import isNil from 'lodash/isNil';
import uniqueId from 'lodash/uniqueId';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as uiSlice from '../../../data/ui.slice';
import {
  DraftCreatableWorkflow,
  EditableWorkflow,
  WorkflowActionType,
  WorkflowState,
} from '../../../data/models/workflow.model';
import ConfirmationDialog from '../../ConfirmationDialog';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import describeWorkflowUtils from '../../../services/describeWorkflowUtils';
import {
  MemoizedWorkflowFlowChart,
  WorkflowChartItem,
} from './WorkflowFlowChart';

interface EditWorkflowProps {
  isCreate: boolean;
}

const PREFIX = 'EditWorkflow';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  menuContainer: `${PREFIX}-menuContainer`,
  chartContainer: `${PREFIX}-chartContainer`,
  chart: `${PREFIX}-chart`,
  step: `${PREFIX}-step`,
  stateSelect: `${PREFIX}-stateSelect`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(3),
  },
  [`& .${classes.header}`]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    gap: 10,
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
  [`& .${classes.menuContainer}`]: {
    justifyContent: 'end',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  [`& .${classes.chartContainer}`]: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  [`& .${classes.chart}`]: {
    width: 600,
  },
  [`& .${classes.step}`]: {
    flex: 1,
    maxWidth: 650,
  },
  [`& .${classes.stateSelect}`]: {
    width: 144,
  },
}));

export default function EditWorkflow({ isCreate }: EditWorkflowProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const existingWorkflow = useAppSelector((state) => {
    const workflow = monitoringSlice.selectWorkflow(
      state,
      searchParams.get('workflowUUID') ?? '',
    );
    if (workflow) {
      return workflowUtils.createWorkflowFormFromModel(workflow);
    }
    return undefined;
  });
  const existingRawWorkflow = useAppSelector((state) => {
    return monitoringSlice.selectWorkflow(
      state,
      searchParams.get('workflowUUID') ?? '',
    );
  });
  const workflowDraft = useAppSelector(monitoringSlice.selectDraftWorkflow);
  const savingWorkflow = useAppSelector(monitoringSlice.selectSavingWorkflow);
  const createdWorkflow = useAppSelector(monitoringSlice.selectCreatedWorkflow);
  const deletedWorkflow = useAppSelector(monitoringSlice.selectDeletedWorkflow);
  const systemEvents = useAppSelector(monitoringSlice.selectSystemEvents);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStepFormData[]>(
    [],
  );
  const [draftStep, setDraftStep] = useState<WorkflowStepFormData | undefined>(
    undefined,
  );
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [nextSelectedWorkflowId, setNextSelectedWorkflowId] = useState('');
  const [addWorkflowDialogOpen, setAddWorkflowDialogOpen] = useState(false);
  const [confirmStatusChangeDialogOpen, setConfirmStatusChangeDialogOpen] =
    useState(false);
  const [confirmDeleteWorkflowDialogOpen, setConfirmDeleteWorkflowDialogOpen] =
    useState(false);
  const [confirmStatusChangeMessage, setConfirmStatusChangeMessage] =
    useState('');
  const [
    confirmStatusChangeDialogPostiveFn,
    setConfirmStatusChangeDialogPostiveFn,
  ] = useState<() => void>(() => null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [removedChartItemId, setRemovedChartItemId] = useState<
    string | undefined
  >();
  const [hideWorkQueueSelector, setHideWorkQueueSelector] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const workflowStepRef = useRef<WorkflowStepRef>();

  const workQueueUUID = searchParams.get('workQueueUUID') ?? '';
  const title = workflowDraft?.name ?? existingWorkflow?.name;
  const description =
    workflowDraft?.description ?? existingWorkflow?.description;

  useEffect(() => {
    dispatch(monitoringSlice.fetchSystemEventsAsync());
  }, []);

  useEffect(() => {
    const workflowUUID = searchParams.get('workflowUUID');
    if (!existingWorkflow && workflowUUID) {
      dispatch(monitoringSlice.fetchWorkflowAsync(workflowUUID));
    }
  }, [existingWorkflow]);

  useEffect(() => {
    if (createdWorkflow) {
      navigate(`/workflows`);
      dispatch(monitoringSlice.setCreatedWorkflow(false));
    }
  }, [createdWorkflow]);

  useEffect(() => {
    if (deletedWorkflow) {
      navigate(`/workflows`);
      dispatch(monitoringSlice.setDeletedWorkflow(false));
    }
  }, [deletedWorkflow]);

  useEffect(() => {
    // Need another state to make sure we dont set the new workflow
    // until the next render after form reset.
    if (nextSelectedWorkflowId) {
      setSelectedWorkflowId(nextSelectedWorkflowId);
    }
  }, [nextSelectedWorkflowId]);

  useEffect(() => {
    // Need to ask again for the workflow name/description if user refreshes page.
    if (!workflowDraft && isCreate) {
      setHideWorkQueueSelector(true);
      setAddWorkflowDialogOpen(true);
    }
  }, [workflowDraft, isCreate]);

  useEffect(() => {
    if (!isCreate && existingWorkflow && workflowSteps.length === 0) {
      if (systemEvents && systemEvents.length > 0) {
        workflowUtils.updateStepStartOnWithSystemEvents(
          existingWorkflow.steps,
          systemEvents,
        );
      }
      setWorkflowSteps(existingWorkflow.steps);
      if (existingWorkflow.steps.length > 0) {
        setSelectedWorkflowId(existingWorkflow.steps[0].tempId ?? '');
      }
    }
  }, [isCreate, existingWorkflow, workflowSteps, systemEvents]);

  useEffect(() => {
    if (isCreate && workflowSteps.length === 0) {
      const startStep: WorkflowStepFormData = {
        ...workflowUtils.getWorkflowStepFormDefaults(),
        name: 'Start',
        isStart: true,
      };
      setWorkflowSteps([startStep]);
      setSelectedWorkflowId(startStep.tempId ?? '');
    }
  }, [isCreate, workflowSteps]);

  const saveCurrentStep = (
    addNewStep: boolean,
    nextId?: string,
    shouldValidate?: boolean,
  ) => {
    const currentStep = workflowStepRef.current?.getFormData();
    let nextSelectedWorkflowId = nextId;
    if (currentStep) {
      const updatedSteps = [...workflowSteps];
      const index = updatedSteps.findIndex(
        (step) => step.tempId === currentStep.tempId,
      );
      updatedSteps[index] = { ...currentStep };
      if (shouldValidate) {
        const workflowData: CreateWorkflowData = {
          workQueueUUID: existingWorkflow?.workQueueUUID ?? workQueueUUID,
          name: title ?? '',
          description,
          steps: updatedSteps,
        };
        try {
          const creatableWorkflow =
            workflowUtils.createWorkflowFromForm(workflowData);
          dispatch(monitoringSlice.validateWorkflowAsync(creatableWorkflow));
        } catch (error) {
          // Workflow validation is advisory, no need to display error if there
          // is an error calling this api
        }
      }
      if (addNewStep) {
        updatedSteps.push(workflowUtils.getWorkflowStepFormDefaults());
        setDraftStep(undefined);
        nextSelectedWorkflowId = updatedSteps[updatedSteps.length - 1].tempId;
      }
      setWorkflowSteps(updatedSteps);
      workflowStepRef.current?.resetForm();

      if (nextSelectedWorkflowId) {
        // Need to another state to make sure we dont set the new workflow
        // until the next render after form reset.
        setNextSelectedWorkflowId(nextSelectedWorkflowId);
      }
      return updatedSteps;
    }
    return workflowSteps;
  };

  const handleAddStepClick = () => {
    if (workflowStepRef.current?.save()) {
      saveCurrentStep(true);
    }
  };

  const handleSaveClick = () => {
    if (workflowStepRef.current?.save()) {
      const steps = saveCurrentStep(false, undefined);
      if (isCreate) {
        const workflowData: CreateWorkflowData = {
          workQueueUUID,
          name: workflowDraft?.name ?? '',
          description: workflowDraft?.description,
          steps,
        };
        try {
          const creatableWorkflow =
            workflowUtils.createWorkflowFromForm(workflowData);
          dispatch(monitoringSlice.createWorkflowAsync(creatableWorkflow));
        } catch (error: any) {
          if (error instanceof WorkflowError) {
            const workflowError = error as WorkflowError;
            dispatch(
              uiSlice.setErrorMessage(
                workflowError.getTranslatedErrorMessage(t),
              ),
            );
          } else {
            dispatch(
              uiSlice.setErrorMessage(t('workflows.createWorkflowError')),
            );
          }
        }
      } else {
        if (!existingWorkflow) {
          return;
        }
        const workflowData: CreateWorkflowData = {
          workQueueUUID: existingWorkflow.workQueueUUID,
          name: title ?? '',
          description,
          steps,
        };
        try {
          const creatableWorkflow =
            workflowUtils.createWorkflowFromForm(workflowData);
          const editableWorkflow = {
            ...creatableWorkflow,
            workflowUUID: existingWorkflow.workflowUUID!,
          };
          dispatch(monitoringSlice.updateWorkflowAsync(editableWorkflow));
        } catch (error: any) {
          if (error instanceof WorkflowError) {
            const workflowError = error as WorkflowError;
            dispatch(
              uiSlice.setErrorMessage(
                workflowError.getTranslatedErrorMessage(t),
              ),
            );
          } else {
            dispatch(
              uiSlice.setErrorMessage(t('workflows.createWorkflowError')),
            );
          }
        }
      }
    }
  };

  const handleAddWorkflowClose = (
    didConfirm?: boolean,
    workflowDraft?: DraftCreatableWorkflow,
  ) => {
    setHideWorkQueueSelector(false);
    setAddWorkflowDialogOpen(false);
    // Immediately save updated name/description
    if (didConfirm && existingRawWorkflow) {
      const updatedWorkflow: EditableWorkflow = {
        name: workflowDraft?.name ?? existingRawWorkflow?.name ?? '',
        description:
          workflowDraft?.description ?? existingRawWorkflow?.description ?? '',
        workQueueUUID: existingRawWorkflow?.workQueueUUID,

        steps: existingRawWorkflow?.steps,
        workflowUUID: existingRawWorkflow?.workflowUUID,
      };
      dispatch(monitoringSlice.updateWorkflowAsync(updatedWorkflow));
    }
  };

  const handleStepClick = (id: string) => {
    if (id !== selectedWorkflowId) {
      if (workflowStepRef.current?.save()) {
        saveCurrentStep(false, id, true);
        return true;
      }
      return false;
    }
    return true;
  };

  const handleMoreOptionsClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setIsMenuOpen(true);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const handleRenameClick = () => {
    dispatch(
      monitoringSlice.setDraftWorkflow({
        name: title ?? '',
        description,
        workQueueUUID: existingWorkflow?.workQueueUUID ?? workQueueUUID,
      }),
    );
    setAddWorkflowDialogOpen(true);
    setIsMenuOpen(false);
  };

  const doesStepHaveReferences = (stepId: string) => {
    const currentStep =
      draftStep ?? workflowSteps.find((step) => step.tempId === stepId);
    const otherSteps = workflowSteps.filter((step) => step.tempId !== stepId);
    if (currentStep) {
      return workflowUtils.doesStepHaveReferences(currentStep, otherSteps);
    }
    return false;
  };

  const handleDeleteStep = (stepId: string) => {
    const updatedSteps = workflowSteps.filter((step) => step.tempId !== stepId);
    workflowUtils.deleteStepReferences(stepId, updatedSteps);
    setWorkflowSteps([...updatedSteps]);
    workflowStepRef.current?.resetForm();
    setRemovedChartItemId(stepId);
  };

  const handleDeleteWorkflow = () => {
    setIsMenuOpen(false);
    setConfirmDeleteWorkflowDialogOpen(true);
  };

  const handleConfirmDeleteWorkflow = () => {
    setConfirmDeleteWorkflowDialogOpen(false);
    if (existingWorkflow?.workflowUUID) {
      dispatch(
        monitoringSlice.deleteWorkflowAsync(existingWorkflow.workflowUUID),
      );
    }
  };

  const updateWorkflowState = (newState: WorkflowState) => {
    setConfirmStatusChangeDialogOpen(false);
    if (
      !existingWorkflow ||
      !existingWorkflow.state ||
      !existingWorkflow.workflowUUID
    ) {
      return;
    }
    // Optimistically update state
    dispatch(
      monitoringSlice.updateWorkflowState({
        workflowUUID: existingWorkflow.workflowUUID,
        state: newState,
      }),
    );
    if (newState === WorkflowState.Active) {
      dispatch(
        monitoringSlice.activateWorkflowAsync(existingWorkflow.workflowUUID),
      );
    } else if (newState === WorkflowState.Inactive) {
      dispatch(
        monitoringSlice.deactivateWorkflowAsync(existingWorkflow.workflowUUID),
      );
    } else if (newState === WorkflowState.Deleted) {
      dispatch(
        monitoringSlice.deleteWorkflowAsync(existingWorkflow.workflowUUID),
      );
    }
  };

  const handleStateChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event;
    if (!existingWorkflow?.workflowUUID) {
      return;
    }
    let newStateText = '';
    const newState = value as WorkflowState;
    switch (newState) {
      case WorkflowState.Active:
        newStateText = t('workflows.activate');
        break;
      case WorkflowState.Inactive:
        newStateText = t('workflows.deactivate');
        break;
      case WorkflowState.Deleted:
        newStateText = t('workflows.delete');
        break;
    }
    setConfirmStatusChangeMessage(
      t('workflows.confirmStatusChangeMessage', { status: newStateText }),
    );
    setConfirmStatusChangeDialogPostiveFn((prevState) => {
      return () => updateWorkflowState(newState);
    });
    setConfirmStatusChangeDialogOpen(true);
  };

  const handleFormUpdate = () => {
    const currentStep = workflowStepRef.current?.getFormData();
    if (currentStep) {
      setDraftStep({ ...currentStep });
    }
  };

  const workflowChartItems: WorkflowChartItem[] = workflowSteps.map((step) => {
    let currentStep = step;
    if (step.tempId === draftStep?.tempId) {
      currentStep = draftStep ?? step;
    }
    const otherSteps: WorkflowStepFormData[] = [];
    workflowSteps.forEach((s) => {
      if (s !== step) {
        if (draftStep && s.tempId === draftStep.tempId) {
          otherSteps.push(draftStep);
        } else {
          otherSteps.push(s);
        }
      }
    });
    const getStepIndex = (id: string) =>
      workflowSteps.findIndex((step) => step.tempId === id);
    const onSuccessId = describeWorkflowUtils.getTransitionStepForActionType(
      WorkflowActionType.Succeed,
      step,
      otherSteps,
    )?.tempId;
    const onFailureId = describeWorkflowUtils.getTransitionStepForActionType(
      WorkflowActionType.Fail,
      step,
      otherSteps,
    )?.tempId;
    const onCancelId = describeWorkflowUtils.getTransitionStepForActionType(
      WorkflowActionType.Cancel,
      step,
      otherSteps,
    )?.tempId;
    const getTransitionObject = (id?: string) => {
      return id ? { id, level: getStepIndex(id) } : undefined;
    };
    return {
      id: step.tempId ?? '',
      title: currentStep.name,
      actions: describeWorkflowUtils.getStepDescription(
        currentStep,
        otherSteps,
        t,
      ),
      row: step.tempId ? getStepIndex(step.tempId) : 0,
      onSuccess: getTransitionObject(onSuccessId),
      onFailure: getTransitionObject(onFailureId),
      onCancel: getTransitionObject(onCancelId),
    };
  });

  let selectedStep: WorkflowStepFormData | undefined;
  if (selectedWorkflowId) {
    selectedStep = workflowSteps.find(
      (step) => step.tempId === selectedWorkflowId,
    );
  }
  if (!selectedStep) {
    selectedStep = workflowUtils.getWorkflowStepFormDefaults();
  }
  const otherSteps = workflowSteps.filter(
    (step) => step.tempId !== selectedWorkflowId,
  );

  let allowedWorkflowStates: WorkflowState[] = [];
  if (existingWorkflow?.state) {
    allowedWorkflowStates = workflowUtils.getAllowedStates(
      existingWorkflow.state,
    );
  }
  const workflowStates = allowedWorkflowStates.map((state) => ({
    key: t(`workflows.${state}`),
    value: state,
  }));

  if (existingWorkflow?.state === WorkflowState.Deleted) {
    return <h1>{t('workflows.workflowDeleted')}</h1>;
  }

  return (
    <StyledPaper elevation={2}>
      <Stack direction="column" className={classes.container}>
        <Stack direction="column" className={classes.header}>
          <Box>
            <Typography variant="h3" className={classes.title}>
              {title}
            </Typography>
          </Box>
          <Typography>{description}</Typography>
        </Stack>

        <Stack direction="row" className={classes.menuContainer}>
          {!isCreate && existingWorkflow && (
            <FormControl className={classes.stateSelect}>
              <InputLabel id="workflow-status-label">
                {t('workflows.status')}
              </InputLabel>
              <Select
                labelId="workflow-status-label"
                onChange={handleStateChange}
                value={existingWorkflow.state}
                disabled={savingWorkflow}>
                {workflowStates.map((state) => (
                  <MenuItem key={state.key} value={state.value}>
                    {state.key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={savingWorkflow}
            onClick={handleSaveClick}>
            {t('common.save')}
          </Button>
          <IconButton
            ref={menuButtonRef}
            size="large"
            onClick={handleMoreOptionsClick}>
            <MoreVertIcon />
          </IconButton>
        </Stack>
        <Divider />
        <Stack direction="row">
          <Box className={classes.chartContainer}>
            <Box className={classes.chart}>
              <MemoizedWorkflowFlowChart
                items={workflowChartItems}
                //selectedIndex={selectedWorkflowIndex}
                selectedId={selectedWorkflowId}
                handleStepClick={handleStepClick}
                handleAddStepClick={handleAddStepClick}
                removedChartItemId={removedChartItemId}
              />
            </Box>
          </Box>
          <Box className={classes.step}>
            <WorkflowStep
              ref={workflowStepRef}
              stepData={selectedStep}
              otherSteps={otherSteps}
              systemEvents={systemEvents}
              handleFormUpdate={handleFormUpdate}
              handleDeleteStep={handleDeleteStep}
              doesStepHaveReferences={doesStepHaveReferences}
            />
          </Box>
        </Stack>
        <AddWorkFlowDialog
          workQueueUUID={workQueueUUID}
          open={addWorkflowDialogOpen}
          close={handleAddWorkflowClose}
          canSave={!!existingRawWorkflow}
          hideWorkQueueSelector={hideWorkQueueSelector}
        />
        <ConfirmationDialog
          title={t('workflows.confirmStatusChangeTitle')}
          message={confirmStatusChangeMessage}
          open={confirmStatusChangeDialogOpen}
          positiveAction={confirmStatusChangeDialogPostiveFn}
          negativeAction={() => setConfirmStatusChangeDialogOpen(false)}
        />
        <ConfirmationDialog
          title={t('workflows.confirmDeleteWorkflowTitle')}
          message={t('workflows.confirmDeleteWorkflowMessage')}
          open={confirmDeleteWorkflowDialogOpen}
          positiveAction={handleConfirmDeleteWorkflow}
          negativeAction={() => setConfirmDeleteWorkflowDialogOpen(false)}
        />
        <Menu
          anchorEl={menuButtonRef.current}
          open={isMenuOpen}
          onClose={handleMenuClose}>
          <MenuItem onClick={handleRenameClick}>
            {t('workflows.rename')}
          </MenuItem>
          {!isCreate &&
            (existingWorkflow?.state === WorkflowState.Inactive ||
              existingWorkflow?.state === WorkflowState.Draft) && (
              <MenuItem
                sx={{
                  color: 'warning.main',
                }}
                onClick={handleDeleteWorkflow}>
                {t('workflows.deleteWorkflow')}
              </MenuItem>
            )}
        </Menu>
      </Stack>
    </StyledPaper>
  );
}
